<template>
  <div>
    <div :style="overlayStyle" class="overlay" @click="onClickOverlay($event)"/>
    <div :id="$attrs['id']" :class="[$attrs['class']]" class="hm-floating-buttons">
      <div class="hm-floating-buttons__wrapper">
        <slot />
      </div>
      <div class="hm-floating-buttons__overlay"></div>
    </div>
  </div>
</template>

<script>

import { isNil, isArray, pull } from 'lodash';
import TimerMixin from '../../../mixins/TimerMixin';

export default {
  name: 'HmFloatingButtons',

  mixins: [TimerMixin],

  data: function () {
    return {
      showOverlay: false,
      overlayOpacity: 0,
      activeChild: null,
      childFloatingButtonCount: 1,
    };
  },

  computed: {
    floatingButtonCount: function () {
      const slot = this.getSlot('default');

      if (isNil(slot)) return 0;
      else if (isArray(slot)) return slot.filter(it => typeof it.tag !== 'undefined').length;
      else return 1;
    },
    overlayStyle: function () {
      return {
        opacity: this.overlayOpacity,
        pointerEvents: this.overlayOpacity === 1 ? 'all' : 'none',
        transition: `opacity ${0.2 * this.childFloatingButtonCount}s ease`,
      };
    },
  },

  watch: {
    activeChild: function (newValue, oldValue) {
      if (newValue !== null) {
        this.childFloatingButtonCount = newValue.floatingButtonCount;
      }
    },
  },

  mounted: function () {
    this.$on('showChildren', (component) => {
      this.activeChild = component;
      this.showOverlay = true;
      this.$nextTick(() => {
        this.overlayOpacity = 1;
      });
    });
    this.$on('hideChildren', () => {
      this.hideOverlay();
    });
  },

  methods: {
    getSlot: function (slotName) {
      return !isNil(this.$slots[slotName]) ? this.$slots[slotName] : this.$scopedSlots[slotName];
    },
    onClickOverlay: function (event) {
      this.$emit('hideChildren');
      this.hideOverlay();
    },
    hideOverlay: function () {
      this.activeChild = null;
      this.overlayOpacity = 0;
      this.setTimeout(() => { this.showOverlay = false; }, 100);
    },
  },
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss">
  @import '~styles/import';

  .overlay {
    position: absolute;
    background-color: rgba(0,0,0, 0.35);
    backdrop-filter: saturate(90%) blur(2px);
    top: 0;
    height: 100%;
    min-height: calc(100vh - 60px);
    left: 0;
    right: 0;
    z-index: 50;
    transition: opacity 0.4s ease;
    opacity: 0;
    pointer-events: none;
  }

  .hm-floating-buttons {
    pointer-events: none;
    user-select: none;
    @media (min-width: $br-tablet){
      padding: 15px 30px;
    }
    @media (min-width: $br-desktop){
      padding: 40px;
    }
    position: fixed;
    right: 0;
    bottom: 0;
    z-index: 1000;
    padding: 15px;
    width: 100%;
    &__wrapper {
      display: grid;
      grid-template-columns: repeat(auto-fit, 70px);
      justify-items: end;
      justify-content: end;
      pointer-events: none;
    }
    .hm-floating-button {
      width: 60px;
      height: 60px;
      background-color: $mint;
      border-radius: 50%;
      box-shadow: 0 6px 15px 0 rgba(0,39,46,0.55);
      position: relative;
      transition: background-color 0.2s ease, opacity 0.2s ease, box-shadow 0.2s ease;
      &:hover {
        cursor: pointer;
      }
      pointer-events: all;
      &--disabled {
        cursor: not-allowed!important;
        background-color: $middlegray!important;
        box-shadow: none!important;
        //box-shadow: inset 0 0px 15px -6px rgba($black,0.35);

        .hm-icon {
          opacity: 0.4;
        }
      }
      &--mint {
        position: relative;
        .hm-floating-button {
          background-color: $mint;
          &:hover {
            background-color: darken($mint, 5%);
          }
        }
      }
      &--blue {
        position: relative;
        .hm-floating-button {
          background-color: $blue;
          &:hover {
            background-color: darken($blue, 5%);
          }
        }
      }
      &--gray {
        position: relative;
        .hm-floating-button {
          background-color: $gray;
          &:hover {
            background-color: darken($gray, 5%);
          }
        }
      }
      &--error {
        position: relative;
        .hm-floating-button {
          background-color: $error;
          &:hover {
            background-color: darken($error, 5%);
          }
        }
      }
      &--orange {
        position: relative;
        .hm-floating-button {
          background-color: $orange;
          &:hover {
            background-color: darken($orange, 5%);
          }
        }
      }
      &--red {
        position: relative;
        .hm-floating-button {
          background-color: $red;
          &:hover {
            background-color: darken($red, 5%);
          }
        }
      }
      &--gray {
        position: relative;
        .hm-floating-button {
          background-color: $gray;
          &:hover {
            background-color: darken($gray, 5%);
          }
        }
      }

      .hm-icon {
        padding: 0.75rem;
        &.icon--line {
          .svg-icon * {
            stroke: $white;
          }
        }
        &.icon--filled {
          .svg-icon * {
            fill: $white;
          }
        }
      }
      &__label {
        position: absolute;
        bottom: calc(100% + 10px);
        left: 50%;
        transform: translateX(-50%);
        white-space: nowrap;
        background: $lightgray;
        border-radius: 5px;
        padding: 4px 10px;
        transition: all 0.2s ease;
        opacity: 0;
        &:before {
          content: '';
          position: absolute;
          left: 50%;
          margin-left: -5px;
          top: 100%;
          width: 0;
          height: 0;
          border-style: solid;
          border-width: 10px 10px 0 10px;
          border-color: $lightgray transparent transparent transparent;
        }
      }
      &__actions {
        pointer-events: all;
        display: grid;
        grid-auto-rows: 50px;
        position: absolute;
        height: auto;
        text-align: right;
        align-items: center;
        bottom: 90px;
        //right: 15px;
        //left: 15px;
        right: 0;
        //width: calc(100% - 30px);
        width: auto;
        @media (max-width: $br-tablet){
          right: -70px;
        }
      }
      &__action {
        display: grid;
        grid-template-columns: 1fr 60px;
        align-items: center;
        opacity: 1;
        cursor: pointer;

        &--disabled {
          cursor: not-allowed!important;
          box-shadow: none!important;
          //box-shadow: inset 0 0px 15px -6px rgba($black,0.35);

          .hm-floating-button__action__label {
            span {
              background-color: $lightgray;
              color: $gray;
            }
          }

          .hm-icon {
            opacity: 0.4;
          }

          .hm-floating-button__action__button {
            background-color: $middlegray;
            box-shadow: none!important;
          }

          &:hover {
            .hm-floating-button__action__button {
              background-color: $middlegray!important;
              box-shadow: none!important;
            }
          }
        }

        &__label {
          padding-right: 10px;
          font-weight: 700;
          font-size: 14px;
          color: $text;
          span {
            padding: 4px 10px;
            background-color: $white;
            border-radius: 3px;
            white-space: nowrap;
            box-shadow: 0 4px 8px 0 rgba($black,0.15);
          }
        }

        &:hover {
          .hm-floating-button__action {
            &__button {
              background-color: darken($blue, 5%);
              transform: scale(1.05);

              &--blue {
                background-color: darken($blue, 5%);
              }

              &--orange {
                background-color: darken($orange, 5%);
              }

              &--error, &--red {
                background-color: darken($error, 5%);
              }

              &--mint {
                background-color: darken($mint, 5%);
              }
            }
          }
        }
        &__button {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          justify-self: center;
          box-shadow: 0 6px 15px 0 rgba($black,0.35);
          transition: background-color 0.2s ease, transform 0.2s ease;

          background-color: $blue;

          .hm-icon {
            align-items: center;
            justify-content: center;
            height: 100%;
            color: $white;
            svg {
              width: 30px;
              height: 30px;
            }
            svg * {
              stroke: $white !important;
            }

            &.icon--line {
              .svg-icon * {
                stroke-width: 1.5;
              }
            }
          }

          &--gray {
            background-color: $gray;
          }

          &--blue {
            background-color: $blue;
          }

          &--mint {
            background-color: $mint;
          }

          &--error, &--red {
            background-color: $error;
          }

          &--orange {
            background-color: $orange;
          }
        }
      }
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/lib/inputs/HmFloatingButtons.vue